import { useState } from "react";
import { Autocomplete, Box, CircularProgress, Divider, IconButton, Stack, TextField, Typography } from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { StringField } from "../ConfigFields/StringField";
import { PdfUploadField } from "../ConfigFields/PdfUploadField";
import PhoneInput, { parsePhoneNumber } from "../../../../common/phone-input";
import ButtonOne from "../../../../common/normal-button";
import { ColorsConstants } from "../../../../cssUtils/constants";
import typography from "../../../../cssUtils/typography";
import { useAuth } from "../../../../hooks/AuthProvider";
import { useTenantCallingCode } from "../../../../hooks/useTenantCountry";
import { MEDIA_ASSET_DELETE } from "../../../../config/urls";

const MAX_OPTIONS = 10;
const MAX_BROCHURES = 3;

const OPTION_TYPES = [
  { value: "send_brochure", label: "Send Brochure" },
  { value: "open_website", label: "Visit Website" },
  { value: "call_support", label: "Call Support" },
  { value: "submit_inquiry", label: "Submit Inquiry" },
  { value: "chat_with_us", label: "Chat With Us" },
  { value: "end_chat", label: "End Chat" },
];

const stripHtml = (html) => (html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const InteractiveMenuPreset = ({ schema, config, handleConfigChange, nodeId, nodes }) => {
  const tenantCallingCode = useTenantCallingCode();
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");
  const [uploadedKeys, setUploadedKeys] = useState({});
  const [deletingKeys, setDeletingKeys] = useState({});
  const [deleteErrors, setDeleteErrors] = useState({});
  const menuOptions = config["menu_options"] || [];

  const updateOptions = (next) => handleConfigChange("menu_options", next);

  const updateOption = (index, patch) => {
    updateOptions(menuOptions.map((opt, i) => (i === index ? { ...opt, ...patch } : opt)));
  };

  const handleAddOption = () => {
    if (menuOptions.length >= MAX_OPTIONS) return;
    updateOptions([...menuOptions, { type: "", button_text: "" }]);
  };

  const handleRemoveOption = (index) => {
    updateOptions(menuOptions.filter((_, i) => i !== index));
  };

  const handleTypeChange = (index, type) => {
    // Reset type-specific fields when switching type so stale config doesn't linger
    const base = {
      type,
      button_text: menuOptions[index]?.button_text || "",
      description: menuOptions[index]?.description || "",
    };
    if (type === "submit_inquiry") base.option_key = menuOptions[index]?.option_key || "";
    if (type === "send_brochure") base.brochures = [];
    if (type === "open_website") base.url = "";
    if (type === "call_support") base.phone_number = "";
    if (type === "end_chat") base.message_node_id = menuOptions[index]?.message_node_id ?? null;
    updateOptions(menuOptions.map((opt, i) => (i === index ? base : opt)));
  };

  const handleAddBrochure = (optionIndex) => {
    const option = menuOptions[optionIndex];
    const brochures = option.brochures || [];
    if (brochures.length >= MAX_BROCHURES) return;
    updateOption(optionIndex, {
      brochures: [
        ...brochures,
        {
          name: "",
          asset_key: `nodebot_${nodeId}_option_${optionIndex}_brochure_${brochures.length}`,
        },
      ],
    });
  };

  const removeBrochureLocally = (optionIndex, brochureIndex) => {
    const option = menuOptions[optionIndex];
    updateOption(optionIndex, {
      brochures: (option.brochures || []).filter((_, i) => i !== brochureIndex),
    });
  };

  const handleRemoveBrochure = async (optionIndex, brochureIndex) => {
    const option = menuOptions[optionIndex];
    const brochure = (option.brochures || [])[brochureIndex];
    const assetKey = brochure?.asset_key;

    if (!window.confirm("Delete this brochure? This will also remove the uploaded file from storage.")) {
      return;
    }

    // No asset_key at all means this brochure was never even staged for
    // upload — nothing on the backend to clean up.
    if (!assetKey) {
      removeBrochureLocally(optionIndex, brochureIndex);
      return;
    }

    setDeletingKeys((prev) => ({ ...prev, [assetKey]: true }));
    setDeleteErrors((prev) => ({ ...prev, [assetKey]: "" }));

    try {
      const token = await getValidToken();
      const response = await fetch(`${MEDIA_ASSET_DELETE}${assetKey}/`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-Id": tenant,
        },
      });
      // 404 means the backend has no MediaAsset for this key (e.g. it was
      // never actually uploaded) — nothing to clean up, so treat it as success.
      if (!response.ok && response.status !== 404) throw new Error("Delete failed");

      setUploadedKeys((prev) => {
        const next = { ...prev };
        delete next[assetKey];
        return next;
      });
      removeBrochureLocally(optionIndex, brochureIndex);
    } catch (err) {
      setDeleteErrors((prev) => ({
        ...prev,
        [assetKey]: "Could not delete the uploaded file. Please try again.",
      }));
    } finally {
      setDeletingKeys((prev) => ({ ...prev, [assetKey]: false }));
    }
  };

  const handleBrochureNameChange = (optionIndex, brochureIndex, name) => {
    const option = menuOptions[optionIndex];
    updateOption(optionIndex, {
      brochures: (option.brochures || []).map((b, i) =>
        i === brochureIndex ? { ...b, name } : b,
      ),
    });
  };

  const loopMessageNodes = (nodes || []).filter(
    (n) => n.node_type_key === "rich_text_message" && n.id !== nodeId,
  );
  // String()-normalized: a node id saved into config before the Flow.js id-type
  // fix could still be a number, while `n.id` here is always a string now —
  // compare as strings so already-configured selections keep resolving.
  const selectedLoopNode =
    loopMessageNodes.find((n) => String(n.id) === String(config["loop_message_node_id"])) || null;

  return (
    <Stack spacing={3}>
      <StringField
        fieldKey="menu_text"
        schema={schema?.properties["menu_text"] || {}}
        value={config["menu_text"]}
        required={true}
        onChange={(val) => handleConfigChange("menu_text", val)}
      />

      <Autocomplete
        options={loopMessageNodes}
        value={selectedLoopNode}
        onChange={(_, newValue) =>
          handleConfigChange("loop_message_node_id", newValue?.id ?? null)
        }
        getOptionLabel={(node) => stripHtml(node?.config?.message).slice(0, 60) || `Node #${node?.id}`}
        isOptionEqualToValue={(node, val) => node?.id === val?.id}
        noOptionsText="No other Rich Text Message nodes in this flow yet"
        renderInput={(params) => (
          <TextField
            {...params}
            label="After Send Brochure / Visit Website / Call Support"
            helperText="Optional. Sends this message then shows the menu again, instead of ending the conversation."
          />
        )}
      />

      <StringField
        fieldKey="store_variable_as"
        schema={schema?.properties["store_variable_as"] || {}}
        value={config["store_variable_as"]}
        required={false}
        onChange={(val) => handleConfigChange("store_variable_as", val)}
      />

      <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
        Menu Options (up to {MAX_OPTIONS})
      </Typography>

      {menuOptions.map((option, index) => {
        // An option without a number yet starts on the tenant's country
        const { countryCode, phoneNo } = option.phone_number
          ? parsePhoneNumber(option.phone_number)
          : { countryCode: `+${tenantCallingCode}`, phoneNo: "" };

        return (
          <Box key={index}>
            {index > 0 && <Divider sx={{ mb: 3 }} />}

            <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mb: 1.5 }}>
              Option {index + 1}
            </Typography>

            <Box
              sx={{
                border: `1px solid ${ColorsConstants.cardBorderPrimaryGrayColor}`,
                borderRadius: 2,
                p: 2,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Autocomplete
                  fullWidth
                  options={OPTION_TYPES}
                  value={OPTION_TYPES.find((t) => t.value === option.type) || null}
                  onChange={(_, newValue) => handleTypeChange(index, newValue?.value || "")}
                  getOptionLabel={(opt) => opt.label || ""}
                  isOptionEqualToValue={(opt, val) => opt.value === val.value}
                  renderInput={(params) => (
                    <TextField {...params} label="Menu Option Type" required />
                  )}
                />
                <IconButton onClick={() => handleRemoveOption(index)} aria-label="Remove option">
                  <Delete sx={{ color: ColorsConstants.error }} />
                </IconButton>
              </Stack>

              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Button Text"
                  value={option.button_text || ""}
                  onChange={(e) => updateOption(index, { button_text: e.target.value })}
                />

                <TextField
                  fullWidth
                  label="Description"
                  helperText="Optional secondary line shown below the button text"
                  value={option.description || ""}
                  onChange={(e) => updateOption(index, { description: e.target.value })}
                />

                {option.type === "submit_inquiry" && (
                  <TextField
                    fullWidth
                    label="Option Key"
                    helperText='Only needed if this menu has more than one Submit Inquiry option — used to route it via a matching outgoing edge condition {"selected_option": "<this value>"}'
                    value={option.option_key || ""}
                    onChange={(e) => updateOption(index, { option_key: e.target.value })}
                  />
                )}

                {option.type === "send_brochure" && (
                  <Stack spacing={2}>
                    {(option.brochures || []).map((brochure, bIndex) => (
                      <Box
                        key={brochure.asset_key || bIndex}
                        sx={{
                          border: `1px solid ${ColorsConstants.secondaryGray}`,
                          borderRadius: 2,
                          p: 1.5,
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <TextField
                            fullWidth
                            label={`Brochure ${bIndex + 1} Name`}
                            value={brochure.name || ""}
                            onChange={(e) =>
                              handleBrochureNameChange(index, bIndex, e.target.value)
                            }
                          />
                          <IconButton
                            onClick={() => handleRemoveBrochure(index, bIndex)}
                            disabled={!!deletingKeys[brochure.asset_key]}
                            aria-label="Remove brochure"
                          >
                            {deletingKeys[brochure.asset_key] ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Delete sx={{ color: ColorsConstants.error }} />
                            )}
                          </IconButton>
                        </Stack>
                        <Box sx={{ mt: 1.5 }}>
                          <PdfUploadField
                            assetKey={brochure.asset_key}
                            uploaded={!!uploadedKeys[brochure.asset_key]}
                            onUploaded={(assetKey) =>
                              setUploadedKeys((prev) => ({ ...prev, [assetKey]: true }))
                            }
                            onFileSelected={(fileName) =>
                              handleBrochureNameChange(index, bIndex, fileName)
                            }
                          />
                        </Box>
                        {deleteErrors[brochure.asset_key] && (
                          <Typography sx={{ color: ColorsConstants.error, mt: 1 }}>
                            {deleteErrors[brochure.asset_key]}
                          </Typography>
                        )}
                      </Box>
                    ))}

                    {(option.brochures || []).length < MAX_BROCHURES && (
                      <ButtonOne
                        title="Add Brochure"
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => handleAddBrochure(index)}
                        sx={{ alignSelf: "flex-start" }}
                      />
                    )}
                  </Stack>
                )}

                {option.type === "open_website" && (
                  <TextField
                    fullWidth
                    label="Website URL"
                    placeholder="https://xyzcompany.com"
                    value={option.url || ""}
                    onChange={(e) => updateOption(index, { url: e.target.value })}
                  />
                )}

                {option.type === "call_support" && (
                  <PhoneInput
                    countryCode={countryCode}
                    phoneNo={phoneNo}
                    onCountryCodeChange={(code) =>
                      updateOption(index, { phone_number: phoneNo ? `${code} ${phoneNo}` : "" })
                    }
                    onPhoneNoChange={(val) => {
                      const trimmed = (val || "").trim();
                      updateOption(index, {
                        phone_number: trimmed ? `${countryCode} ${trimmed}` : "",
                      });
                    }}
                    label="Support Phone Number"
                    fullWidth
                  />
                )}

                {option.type === "end_chat" && (
                  <Autocomplete
                    options={loopMessageNodes}
                    value={
                      loopMessageNodes.find((n) => String(n.id) === String(option.message_node_id)) || null
                    }
                    onChange={(_, newValue) =>
                      updateOption(index, { message_node_id: newValue?.id ?? null })
                    }
                    getOptionLabel={(node) =>
                      stripHtml(node?.config?.message).slice(0, 60) || `Node #${node?.id}`
                    }
                    isOptionEqualToValue={(node, val) => node?.id === val?.id}
                    noOptionsText="No Rich Text Message nodes in this flow yet"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Message Before Ending"
                        helperText="Optional. Sends this message, then ends the conversation (never loops back to the menu)."
                      />
                    )}
                  />
                )}
              </Stack>
            </Box>
          </Box>
        );
      })}

      {menuOptions.length < MAX_OPTIONS && (
        <ButtonOne
          title="Add Menu Option"
          variant="outlined"
          startIcon={<Add />}
          onClick={handleAddOption}
          sx={{ alignSelf: "flex-start" }}
        />
      )}
    </Stack>
  );
};

export default InteractiveMenuPreset;
